import classNames from "classnames";
import isNil from "lodash/isNil";
import { ProPilotMessage } from "PFApp/stores/propilot_store";
import { Typography } from "PFComponents/typography";
import { ReactNode, useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";

import css from "./propilot_message_list.module.scss";

type ProPilotMessageListProps = {
  messages: ProPilotMessage[];
  renderMessage?: (message: ProPilotMessage) => ReactNode;
};

const defaultRenderMessage = (message: ProPilotMessage): ReactNode => {
  if (message.content.type !== "generic") {
    // system_error content is rendered via MessageRenderer, not the default renderer.
    return null;
  }
  const { text } = message.content;
  if (isNil(text)) {
    return null;
  }
  const isAuthorUser = message.author === "user";
  return (
    <div
      className={classNames(css.messageItem, {
        [css.messageItemUser]: isAuthorUser,
        [css.messageItemAgent]: !isAuthorUser
      })}
      data-qa-id="ProPilotMessageItem"
    >
      {text}
    </div>
  );
};

export const ProPilotMessageList = ({ messages, renderMessage }: ProPilotMessageListProps) => {
  const { t } = useTranslation("translation");
  const emptyId = useId();
  const render = renderMessage ?? defaultRenderMessage;
  const listRef = useRef<HTMLDivElement | null>(null);
  // Tracks whether the user is near the bottom; updated via scroll event listener (not during render).
  const isNearBottomRef = useRef(true);

  // Listen for manual scrolling to track whether auto-scroll should fire.
  useEffect(() => {
    const el = listRef.current;
    if (!el) {
      return;
    }
    const SCROLL_THRESHOLD_PX = 80;
    const handleScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      isNearBottomRef.current = distanceFromBottom <= SCROLL_THRESHOLD_PX;
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-scroll to bottom on new messages, but only when the user is already near the bottom.
  // Mirrors ChatGPT-style behaviour: reading history higher up is not interrupted.
  useEffect(() => {
    if (isNearBottomRef.current && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages.length]);

  if (messages.length === 0) {
    return (
      <div ref={listRef} role="list" aria-label={t("propilot.messageList.label")} className={css.list}>
        <Typography
          variant="bodyRegular"
          id={emptyId}
          className={css.emptyState}
          qaId="ProPilotMessageList.empty"
        >
          {t("propilot.messageList.empty")}
        </Typography>
      </div>
    );
  }

  return (
    <div ref={listRef} role="list" aria-label={t("propilot.messageList.label")} className={css.list}>
      <div className={css.spacer} aria-hidden="true" />
      {messages.map((message) => (
        <div
          key={message.id}
          role="listitem"
          className={message.author === "user" ? css.messageRowUser : css.messageRowAgent}
        >
          {render(message)}
        </div>
      ))}
    </div>
  );
};
